import {ActionIcon, Text, Tooltip} from "@mantine/core";
import {
    useCallback,
    useEffect,
    useRef,
    useState,
    type KeyboardEvent,
    type PointerEvent as ReactPointerEvent,
    type ReactNode,
} from "react";
import {useTranslation} from "react-i18next";
import CloseIcon from "../../../assets/x.svg?react";
import IconArrowUpRight from "../../../assets/arrow-up-right.svg?react";
import {ExperimentalLink} from "../../../components/ExperimentalLink";
import {resolveDibHref} from "../../../utils/dibUrls";
import {noteIslandToPage} from "../../content/topicEntryNote";
import {targetFromSource} from "../citation-preview/useCitationParts";
import {useWorkIsland, type WorkIslandRequest} from "./WorkIslandContext";
import classes from "./WorkIslandLayout.module.css";

type WorkIslandLayoutProps = {
    children: ReactNode;
    renderContent: (
        request: WorkIslandRequest,
        compactToc: boolean
    ) => ReactNode;
};

export function WorkIslandLayout({
    children,
    renderContent,
}: WorkIslandLayoutProps) {
    const {t} = useTranslation();
    const {request, contentFraction, closeIsland, setMode, setContentFraction} =
        useWorkIsland();
    const layoutRef = useRef<HTMLDivElement>(null);
    const islandRef = useRef<HTMLElement>(null);
    // The table of contents collapses to the rail under 42rem of island width.
    const [compactToc, setCompactToc] = useState(false);

    useEffect(() => {
        const island = islandRef.current;
        if (!island || typeof ResizeObserver === "undefined") return;
        const limit = 42 * parseFloat(getComputedStyle(document.documentElement).fontSize || "16");
        const update = () => setCompactToc(island.getBoundingClientRect().width < limit);
        update();
        const observer = new ResizeObserver(update);
        observer.observe(island);
        return () => observer.disconnect();
    }, [request]);

    const resizeAt = useCallback(
        (clientX: number) => {
            const bounds = layoutRef.current?.getBoundingClientRect();
            if (!bounds?.width) return;
            setContentFraction((bounds.right - clientX) / bounds.width);
        },
        [setContentFraction]
    );

    const handlePointerDown = useCallback(
        (event: ReactPointerEvent<HTMLDivElement>) => {
            event.preventDefault();
            event.currentTarget.setPointerCapture(event.pointerId);
            resizeAt(event.clientX);
        },
        [resizeAt]
    );

    const handlePointerEnd = useCallback(
        (event: ReactPointerEvent<HTMLDivElement>) => {
            if (event.currentTarget.hasPointerCapture(event.pointerId)) {
                event.currentTarget.releasePointerCapture(event.pointerId);
            }
        },
        []
    );

    const handlePointerMove = useCallback(
        (event: ReactPointerEvent<HTMLDivElement>) => {
            if (event.currentTarget.hasPointerCapture(event.pointerId)) {
                resizeAt(event.clientX);
            }
        },
        [resizeAt]
    );

    const handleResizeKey = useCallback(
        (event: KeyboardEvent<HTMLDivElement>) => {
            const delta = event.shiftKey ? 0.1 : 0.05;
            if (event.key === "ArrowLeft") {
                event.preventDefault();
                setContentFraction(contentFraction + delta);
            } else if (event.key === "ArrowRight") {
                event.preventDefault();
                setContentFraction(contentFraction - delta);
            } else if (event.key === "Home") {
                event.preventDefault();
                setMode("content-wide");
            } else if (event.key === "End") {
                event.preventDefault();
                setMode("chat-wide");
            }
        },
        [contentFraction, setContentFraction, setMode]
    );

    const source = request?.source;
    const title = source?.segmentName
        ? `${source.name} | ${source.segmentName}`
        : (source?.name ?? "");
    const description = source?.description || source?.type;
    const fullTopicHref = resolveDibHref(source?.url);
    // The full page continues the island's view if the island has counted it.
    const noteContinuation = () =>
        noteIslandToPage(source && targetFromSource(source)?.resourceId);

    // Split off the title's last word so the arrow can stay next to it and
    // stay on the same line, even when the title wraps.
    const lastSpaceIndex = title.lastIndexOf(" ");
    const titleLead =
        lastSpaceIndex === -1 ? "" : title.slice(0, lastSpaceIndex + 1);
    const titleLastWord =
        lastSpaceIndex === -1 ? title : title.slice(lastSpaceIndex + 1);

    const headingContent = (
        <>
            <Text className={classes.title} lineClamp={1}>
                {titleLead}
                <span className={classes.lastWord}>
                    {titleLastWord}
                    {fullTopicHref && (
                        <IconArrowUpRight
                            aria-hidden="true"
                            className={classes.arrow}
                        />
                    )}
                </span>
            </Text>
            {description && (
                <Text className={classes.subtitle} lineClamp={1}>
                    {description}
                </Text>
            )}
        </>
    );

    return (
        <div
            ref={layoutRef}
            className={`${classes.layout} ${request ? classes.layoutOpen : ""}`}
        >
            <div
                className={classes.chatPane}
                style={{
                    flexBasis: request
                        ? `${(1 - contentFraction) * 100}%`
                        : "100%",
                }}
            >
                {children}
            </div>

            {request && (
                <>
                    <div
                        className={classes.separator}
                        role="separator"
                        aria-label={t("WorkIsland.Resize")}
                        aria-orientation="vertical"
                        aria-valuemin={30}
                        aria-valuemax={70}
                        aria-valuenow={Math.round(contentFraction * 100)}
                        tabIndex={0}
                        onPointerDown={handlePointerDown}
                        onPointerMove={handlePointerMove}
                        onPointerUp={handlePointerEnd}
                        onPointerCancel={handlePointerEnd}
                        onKeyDown={handleResizeKey}
                    />

                    <section
                        ref={islandRef}
                        className={classes.island}
                        style={{flexBasis: `${contentFraction * 100}%`}}
                        aria-label={title}
                    >
                <header className={classes.header}>
                    {fullTopicHref ? (
                        <ExperimentalLink
                            href={fullTopicHref}
                            className={`${classes.headingText} ${classes.titleLink}`}
                            onClick={noteContinuation}
                        >
                            {headingContent}
                        </ExperimentalLink>
                    ) : (
                        <div className={classes.headingText}>
                            {headingContent}
                        </div>
                    )}
                    <Tooltip label={t("Action.Close")}>
                        <ActionIcon
                            className={classes.closeButton}
                            variant="subtle"
                            color="gray"
                            size={32}
                            radius={0}
                            aria-label={t("WorkIsland.CloseContent")}
                            onClick={closeIsland}
                        >
                            <CloseIcon />
                        </ActionIcon>
                    </Tooltip>
                </header>
                <div className={classes.content}>
                    {renderContent(request, compactToc)}
                </div>
                    </section>
                </>
            )}
        </div>
    );
}
